import React from 'react';
import { InputText, InputTextProps } from 'primereact/inputtext';
import { InputTextarea, InputTextareaProps } from 'primereact/inputtextarea';

type GFNInputType = 'text' | 'email' | 'password' | 'number' | 'search' | 'textarea';

interface GFNInputProps
  extends Omit<InputTextProps & InputTextareaProps, 'type'> {
  /**
   * Input type
   * @default 'text'
   */
  type?: GFNInputType;
  /**
   * Input size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Full width input
   * @default true
   */
  fullWidth?: boolean;
  /**
   * Custom test ID for testing
   */
  testId?: string;
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Error message
   */
  errorMessage?: string;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * Input rows (for textarea)
   */
  rows?: number;
}

/**
 * GFN Input Component
 * 
 * A standardized input component that inherits from the central PrimeReact theme.
 * Ensures consistent styling for all text inputs, email inputs, and textareas.
 * 
 * @example
 * <GFNInput
 *   type="email"
 *   placeholder="Enter your email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 * />
 */
const GFNInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  GFNInputProps
>(
  (
    {
      type = 'text',
      size = 'md',
      fullWidth = true,
      testId,
      error = false,
      errorMessage,
      label,
      rows = 3,
      className = '',
      ...restProps
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'p-inputtext-sm',
      md: '',
      lg: 'p-inputtext-lg',
    };

    const combinedClassName = [
      sizeClasses[size],
      fullWidth && 'w-full',
      error && 'p-invalid',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const commonProps = {
      ref: ref as any,
      className: combinedClassName,
      'data-testid': testId,
      ...restProps,
    };

    return (
      <div className="flex flex-col gap-sm">
        {label && <label className="font-bold">{label}</label>}
        {type === 'textarea' ? (
          <InputTextarea
            rows={rows}
            {...commonProps}
          />
        ) : (
          <InputText
            type={type as any}
            {...commonProps}
          />
        )}
        {error && errorMessage && (
          <small className="text-danger">{errorMessage}</small>
        )}
      </div>
    );
  }
);

GFNInput.displayName = 'GFNInput';

export default GFNInput;
export { GFNInput };
